export type OfferKind =
  | 'nav'
  | 'build'
  | 'page'
  | 'report'
  // 'custom' (post-chart "open as custom page") is no longer offered anywhere;
  // it stays parseable so a stray marker degrades to plain text, never raw
  // `[phrase](offer:custom)` markup.
  | 'custom'
  | 'filters'
  // 'geo_tax' offers a one-click switch of the GLOBAL geography taxonomy
  // preference (metro ↔ administrative), disclosed when the assistant had to
  // substitute a geography that only exists in the user's other taxonomy.
  | 'geo_tax';

/**
 * Matches `[visible phrase](offer:<kind>)` for the kinds above, with an
 * optional machine target after a colon — `[Attrition Report](offer:report:Attrition)`.
 * Groups: 1 = visible phrase, 2 = kind, 3 = target id (absent on untargeted
 * markers). The target is an id the model copied from its own tool call.
 */
export const OFFER_MARKER_REGEX =
  /\[([^\]]+)\]\(offer:(nav|build|page|report|custom|filters|geo_tax)(?::([A-Za-z][A-Za-z0-9_]*))?\)/g;

/** True when `text` contains at least one offer trigger of `kind`. */
export function hasOfferMarker(text: string, kind: OfferKind): boolean {
  // Fresh regex each call — the global flag carries lastIndex state.
  const re = new RegExp(OFFER_MARKER_REGEX.source, 'g');
  for (const m of text.matchAll(re)) {
    if (m[2] === kind) {
      return true;
    }
  }
  return false;
}

/** The target ids of every `kind` marker in `text`, in order (untargeted ones skipped). */
export function offerMarkerTargets(text: string, kind: OfferKind): string[] {
  const re = new RegExp(OFFER_MARKER_REGEX.source, 'g');
  const targets: string[] = [];
  for (const m of text.matchAll(re)) {
    if (m[2] === kind && m[3]) {
      targets.push(m[3]);
    }
  }
  return targets;
}

/**
 * Demote offer triggers in `text` to their visible phrase. With `kind`, only
 * that trigger kind is removed; without it, every offer marker is removed.
 * Keeping this behavior here gives server and client recovery paths one parser.
 */
export function stripOfferMarkers(text: string, kind?: OfferKind): string {
  return text.replace(
    new RegExp(OFFER_MARKER_REGEX.source, 'g'),
    (match, phrase: string, matchedKind: string) =>
      kind === undefined || matchedKind === kind ? phrase : match
  );
}
